import { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import {
  GET_OPTION_ITEM_INGREDIENTS,
  GET_RAW_MATERIALS,
} from '../../graphql/costing-queries'
import {
  CREATE_OPTION_ITEM_INGREDIENT,
  DELETE_OPTION_ITEM_INGREDIENT,
} from '../../graphql/costing-mutations'
import { OptionItemIngredient, RawMaterial } from '../../types/costing'
import LoadingSpinner from '../LoadingSpinner'
import toast from 'react-hot-toast'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'

interface Props {
  optionItemId: number
  optionItemLabel: string
}

const OptionItemIngredientsTab = ({ optionItemId, optionItemLabel }: Props) => {
  const { hasPermission } = usePermissions()
  const canManage = hasPermission(PERMISSIONS.COSTING_MANAGE)

  const [rawMaterialId, setRawMaterialId] = useState('')
  const [quantity, setQuantity] = useState('')

  const { data: ingredientsData, loading } = useQuery(GET_OPTION_ITEM_INGREDIENTS, {
    variables: { optionItemId },
  })

  const { data: allMaterialsData } = useQuery(GET_RAW_MATERIALS, {
    variables: { filters: { active: true } },
    skip: !canManage,
  })

  const refetchVars = [{ query: GET_OPTION_ITEM_INGREDIENTS, variables: { optionItemId } }]

  const [createIngredient, { loading: adding }] = useMutation(CREATE_OPTION_ITEM_INGREDIENT, {
    refetchQueries: refetchVars,
    onCompleted: () => {
      toast.success('Ingredient effect added')
      setRawMaterialId('')
      setQuantity('')
    },
    onError: (err) => toast.error(`Failed: ${err.message}`),
  })

  const [deleteIngredient] = useMutation(DELETE_OPTION_ITEM_INGREDIENT, {
    refetchQueries: refetchVars,
    onCompleted: () => toast.success('Ingredient effect removed'),
    onError: (err) => toast.error(`Failed: ${err.message}`),
  })

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault()
    const qty = parseFloat(quantity)
    if (!rawMaterialId) { toast.error('Select an ingredient'); return }
    if (isNaN(qty) || qty === 0) { toast.error('Enter a non-zero quantity (negative to remove)'); return }
    createIngredient({
      variables: {
        optionItemId,
        data: {
          raw_material_id: parseInt(rawMaterialId),
          quantity: qty,
        },
      },
    })
  }

  const ingredients: OptionItemIngredient[] = ingredientsData?.optionItemIngredients ?? []
  const allMaterials: RawMaterial[] = allMaterialsData?.rawMaterials ?? []

  const getSelectedUnit = (id: string) => allMaterials.find((m) => m.id === parseInt(id))?.unit ?? ''

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Info */}
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
        <p>
          These define the ingredient effect when a customer selects <strong>{optionItemLabel}</strong>.
          Use a positive quantity to add an ingredient, negative to remove it.
          Rows without a variant scope apply to all variants; rows with a variant scope override for that size.
        </p>
      </div>

      {/* Ingredient effects table */}
      {ingredients.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center">
          <p className="text-sm text-gray-500">No ingredient effects defined for this option item.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ingredient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scope</th>
                {canManage && <th className="relative px-6 py-3"><span className="sr-only">Remove</span></th>}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ingredients.map((ing) => {
                const mat = ing.raw_material
                return (
                  <tr key={ing.id}>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">
                      {mat ? (
                        <>
                          {mat.name}
                          <span className="ml-1 text-xs text-gray-400">({mat.unit})</span>
                        </>
                      ) : (
                        <span className="text-red-600">(Deleted material #{ing.raw_material_id})</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-700">
                      {mat ? (
                        <span className={ing.quantity < 0 ? 'text-red-600' : 'text-green-700'}>
                          {ing.quantity > 0 ? '+' : ''}{ing.quantity} {mat.unit}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                      </td>
                    <td className="px-6 py-3 text-sm text-gray-500">
                      {ing.variant_id != null
                        ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                            {ing.variant
                              ? `${ing.variant.product?.name ?? '?'} — ${ing.variant.name}`
                              : `variant #${ing.variant_id}`}
                          </span>
                        : <span className="text-gray-400 text-xs">all variants</span>
                      }
                    </td>
                    {canManage && (
                      <td className="px-6 py-3 text-right">
                        <button
                          onClick={() => deleteIngredient({ variables: { id: ing.id } })}
                          className="text-gray-400 hover:text-red-600 p-1 rounded"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add form */}
      {canManage && (
        <div className="card p-6 space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Add Ingredient Effect</h3>
          <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs text-gray-500 mb-1">Ingredient affected</label>
              <select
                value={rawMaterialId}
                onChange={(e) => setRawMaterialId(e.target.value)}
                className="input w-full"
              >
                <option value="">Select ingredient…</option>
                {allMaterials.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.unit})
                  </option>
                ))}
              </select>
            </div>

            <div className="w-36">
              <label className="block text-xs text-gray-500 mb-1">Quantity</label>
              <div className="relative">
                <input
                  type="number"
                  step="any"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="5"
                  className="input w-full pr-8"
                />
                {rawMaterialId && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    {getSelectedUnit(rawMaterialId)}
                  </span>
                )}
              </div>
            </div>

            <div className="self-end">
              <button type="submit" disabled={adding} className="btn-primary">
                <PlusIcon className="h-4 w-4 mr-1" />
                {adding ? 'Adding…' : 'Add Effect'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}

export default OptionItemIngredientsTab
